import { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { usePermissions } from "@/lib/permissions";
import { vehicleService, driverService, tripService, alertService, geofenceService, fleetService } from "@/services/api";
import { MapCanvas } from "@/components/common/MapCanvas";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Telemetry, StatItem } from "@/components/common/Telemetry";
import { SkeletonMap } from "@/components/common/Skeletons";
import { EmptyState } from "@/components/common/EmptyState";
import { AreaTrend } from "@/components/common/Charts";
import { AddVehicleDialog } from "@/components/dialogs/AddVehicleDialog";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { relativeTime, dateTime, clockTime, fmtKm, fmtDuration, fmtL, compassDirection } from "@/lib/format";
import { useVehicleAddress } from "@/hooks/useVehicleAddress";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft, MapPin, Cpu, User, Fingerprint, Battery, Clock, Crosshair, Route as RouteIcon,
  CheckCircle2, ShieldCheck, Wrench, Fuel, Gauge, TrendingDown, TrendingUp, Bell, Navigation,
  Pencil, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const TABS = ["overview", "live", "trips", "health", "alerts", "history"];

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isManager, isAdmin } = usePermissions();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "overview";
  const { data: v, loading, refetch } = useFetch(() => vehicleService.get(id), [id]);
  const { data: geofences } = useFetch(() => geofenceService.list(), []);
  const { data: distanceSeries } = useFetch(() => fleetService.distanceSeries(id), [id]);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Up here with the other hooks, above the early returns below — a hook after
  // a conditional return would break on the very first render. The vehicle
  // payload carries no address (resolving one is a billed lookup, so it is not
  // folded into every list row); this fetches it for this one vehicle and the
  // Live Location panel reuses the same result.
  const { address, resolving: resolvingAddress } = useVehicleAddress(
    id,
    v?.location?.lat,
    v?.location?.lng,
    { moving: v?.status === "moving" },
  );

  // `!v` so this is the first-load skeleton only. On a bare `loading` check,
  // refetch (after an edit or driver assignment) replaced the whole page with
  // a skeleton, remounting the Live Location map and resetting its zoom and
  // position back to the default.
  if (loading && !v) {
    return <div className="p-6"><SkeletonMap className="h-40" /><div className="mt-4 h-[400px]"><SkeletonMap /></div></div>;
  }
  if (!v) {
    return <div className="p-8"><EmptyState title="Vehicle not found" description="This vehicle may have been removed." action="Back to Vehicles" onAction={() => navigate("/vehicles")} /></div>;
  }

  const driver = v.driverId ? driverService.byId(v.driverId) : null;
  const trips = tripService.forVehicle(v.id);
  const vAlerts = alertService.forVehicle(v.id);

  const setTab = (t) => setParams({ tab: t });

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await vehicleService.remove(v.id);
      toast.success("Vehicle removed");
      navigate("/vehicles");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not remove vehicle");
      setDeleting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <button onClick={() => navigate("/vehicles")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground" data-testid="vehicle-back">
        <ArrowLeft className="h-4 w-4" /> Vehicles
      </button>

      {/* Cockpit header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
            {v.name} <span className="text-muted-foreground">— {v.fleetTag}</span>
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {v.status === "offline" ? "Disconnected" : "Connected"}</span>
            <span>·</span>
            <StatusBadge status={v.status} size="sm" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setTab("live")}><Crosshair className="mr-1.5 h-4 w-4" /> Track Live</Button>
          {driver && <Button variant="secondary" onClick={() => navigate(`/drivers/${driver.id}`)}><User className="mr-1.5 h-4 w-4" /> Driver</Button>}
          {isManager && (
            <Button variant="secondary" onClick={() => setEditOpen(true)} data-testid="edit-vehicle"><Pencil className="mr-1.5 h-4 w-4" /> Edit</Button>
          )}
          {isAdmin && (
            <Button variant="destructive" onClick={() => setDeleteOpen(true)} data-testid="delete-vehicle"><Trash2 className="mr-1.5 h-4 w-4" /></Button>
          )}
        </div>
      </div>

      {/* Key facts */}
      <div className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-border/70 bg-border/50 md:grid-cols-4 xl:grid-cols-7">
        <Fact icon={Cpu} label="Device" value={v.device.id} />
        <Fact icon={MapPin} label="Location" value={address || v.location.address} truncate />
        <Fact icon={User} label="Driver" value={driver?.name || "Unassigned"} />
        <Fact icon={Fingerprint} label="VIN" value={v.vin} mono truncate />
        <Fact icon={Gauge} label="Make / Model" value={`${v.make} ${v.model}`} />
        <Fact icon={Battery} label="Battery" value={BATTERY_LABEL[v.batteryStatus] || "Unknown"} />
        <Fact icon={Clock} label="Last Seen" value={relativeTime(v.lastSeen)} />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="w-full justify-start overflow-x-auto no-scrollbar" data-testid="vehicle-tabs">
          {TABS.map((t) => <TabsTrigger key={t} value={t} className="capitalize" data-testid={`vehicle-tab-${t}`}>{t === "live" ? "Live Location" : t}</TabsTrigger>)}
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <Card className="border-border/70 bg-card/60 p-5">
              <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
                <Telemetry value={v.speed} unit="mph" label="Current Speed" accent={v.status === "moving"} />
                <Telemetry value={BATTERY_LABEL[v.batteryStatus] || "Unknown"} label="Battery" />
                <Telemetry value={v.todayDistance.toFixed(1)} unit="mi" label="Today's Distance" />
                <Telemetry value={fmtDuration(v.todayDriveTime)} label="Drive Time" />
              </div>
              <div className="mt-6">
                <div className="mb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Distance · last 7 days</div>
                <AreaTrend data={distanceSeries || []} x="day" y="km" unit="mi" height={180} />
              </div>
            </Card>
            <Card className="overflow-hidden border-border/70 bg-card/60">
              <div className="border-b border-border/60 p-4"><h3 className="font-display text-base font-medium">Current location</h3><p className="text-xs text-muted-foreground">{address || v.location.address}</p></div>
              <div className="h-[240px] p-3"><MapCanvas vehicles={[v]} geofences={geofences || []} controls={false} selectedId={v.id} className="h-full" /></div>
            </Card>
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatItem label="Fuel consumed" value={fmtL(v.fuelConsumed)} />
            <StatItem label="Idle time" value={fmtDuration(v.idleTime)} />
            <StatItem label="Hard braking" value={v.hardBraking} sub="events today" />
            <StatItem label="Hard acceleration" value={v.hardAccel} sub="events today" />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <RecentTrips trips={trips} navigate={navigate} />
            <RecentAlerts alerts={vAlerts} />
          </div>
        </TabsContent>

        {/* LIVE LOCATION */}
        <TabsContent value="live" className="mt-6">
          <LiveLocation v={v} geofences={geofences || []} address={address} resolving={resolvingAddress} />
        </TabsContent>

        {/* TRIPS */}
        <TabsContent value="trips" className="mt-6">
          {trips.length === 0 ? <EmptyState title="No trips yet" description="This vehicle has no recorded trips." /> : (
            <div className="space-y-3">{trips.map((t) => <TripRow key={t.id} t={t} onClick={() => navigate(`/trips/${t.id}`)} />)}</div>
          )}
        </TabsContent>

        {/* HEALTH */}
        <TabsContent value="health" className="mt-6"><VehicleHealth v={v} alerts={vAlerts} /></TabsContent>

        {/* ALERTS */}
        <TabsContent value="alerts" className="mt-6">
          {vAlerts.length === 0 ? <EmptyState icon={Bell} title="No active alerts" description="This vehicle has no alerts." /> : (
            <div className="space-y-3">{vAlerts.map((a) => <AlertRow key={a.id} a={a} />)}</div>
          )}
        </TabsContent>

        {/* HISTORY */}
        <TabsContent value="history" className="mt-6"><VehicleHistory trips={trips} alerts={vAlerts} /></TabsContent>
      </Tabs>

      <AddVehicleDialog open={editOpen} onOpenChange={setEditOpen} vehicle={v} onSaved={refetch} />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Remove this vehicle?"
        description={`This will permanently remove "${v.name}" and unlink its device. This cannot be undone.`}
        confirmLabel={deleting ? "Removing…" : "Remove"}
        destructive
        onConfirm={handleDelete}
      />
    </div>
  );
}

function Fact({ icon: Icon, label, value, mono, truncate }) {
  return (
    <div className="bg-card/70 p-4">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className={cn("mt-1.5 text-sm font-medium", mono && "font-telemetry text-xs", truncate && "truncate")} title={value}>{value}</div>
    </div>
  );
}

function RecentTrips({ trips, navigate }) {
  return (
    <Card className="border-border/70 bg-card/60 p-5">
      <h3 className="mb-3 font-display text-base font-medium">Recent trips</h3>
      {trips.length === 0 ? <p className="text-sm text-muted-foreground">No trips recorded.</p> : (
        <div className="space-y-2">
          {trips.slice(0, 4).map((t) => (
            <button key={t.id} onClick={() => navigate(`/trips/${t.id}`)} className="flex w-full items-center gap-3 rounded-xl border border-border/50 bg-secondary/20 p-3 text-left transition hover:border-border hover:bg-secondary/40">
              <RouteIcon className="h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{t.startLoc} → {t.endLoc}</div>
                <div className="text-xs text-muted-foreground">{clockTime(t.startTime, t.utcOffsetMinutes)} · {fmtKm(t.distanceKm)} · {fmtDuration(t.durationMin)}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}

function RecentAlerts({ alerts }) {
  return (
    <Card className="border-border/70 bg-card/60 p-5">
      <h3 className="mb-3 font-display text-base font-medium">Recent alerts</h3>
      {alerts.length === 0 ? <p className="text-sm text-muted-foreground">No alerts.</p> : (
        <div className="space-y-2">{alerts.slice(0, 4).map((a) => <AlertRow key={a.id} a={a} compact />)}</div>
      )}
    </Card>
  );
}

function AlertRow({ a, compact }) {
  return (
    <div className={cn("flex items-start gap-3 rounded-xl border border-border/50 bg-secondary/20 p-3", !compact && "bg-card/40")}>
      <div className={cn("mt-0.5 h-2 w-2 shrink-0 rounded-full", a.severity === "critical" ? "bg-red-500" : a.severity === "warning" ? "bg-amber-400" : "bg-sky-400")} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium">{a.type}</span>
          <span className="text-xs text-muted-foreground">{relativeTime(a.time)}</span>
        </div>
        {/* When it happened, not where the vehicle is now — "3 days ago" on
            its own can't be lined up against a shift or a delivery window. */}
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {dateTime(a.time)}{a.geofenceName ? ` · ${a.geofenceName}` : ""}
        </p>
      </div>
    </div>
  );
}

function TripRow({ t, onClick }) {
  return (
    <button onClick={onClick} className="flex w-full items-center gap-4 rounded-2xl border border-border/60 bg-card/40 p-4 text-left transition hover:border-border hover:bg-card/70">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary/50"><RouteIcon className="h-5 w-5 text-muted-foreground" /></div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{t.startLoc} → {t.endLoc}</div>
        <div className="text-xs text-muted-foreground">{clockTime(t.startTime, t.utcOffsetMinutes)}{t.endTime ? ` – ${clockTime(t.endTime, t.utcOffsetMinutes)}` : " · in progress"}</div>
      </div>
      <div className="hidden gap-6 sm:flex">
        <div className="text-right"><div className="font-telemetry text-sm">{fmtKm(t.distanceKm)}</div><div className="text-[10px] uppercase text-muted-foreground">Distance</div></div>
        <div className="text-right"><div className="font-telemetry text-sm">{fmtDuration(t.durationMin)}</div><div className="text-[10px] uppercase text-muted-foreground">Duration</div></div>
        <div className="text-right"><div className="font-telemetry text-sm">{t.maxSpeed.toFixed(1)}</div><div className="text-[10px] uppercase text-muted-foreground">Max mph</div></div>
      </div>
    </button>
  );
}

function LiveLocation({ v, geofences, address, resolving }) {
  const [route, setRoute] = useState(false);
  const trips = tripService.forVehicle(v.id);
  const latestTripId = trips[0]?.id || null;
  const { data: routePts } = useFetch(() => (latestTripId ? tripService.route(latestTripId) : Promise.resolve(null)), [latestTripId]);
  const near = geofences.filter((g) => g.status === "active").slice(0, 3);
  const hasFix = v.location.lat != null && v.location.lng != null;
  const coords = hasFix ? `${v.location.lat.toFixed(4)}, ${v.location.lng.toFixed(4)}` : null;
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
      <div className="h-[520px]">
        <MapCanvas vehicles={[v]} geofences={geofences} selectedId={v.id} route={route ? routePts : null} className="h-full" testId="live-location-map" />
      </div>
      <Card className="flex h-[520px] flex-col border-border/70 bg-card/60">
        <div className="space-y-5 overflow-y-auto p-5 no-scrollbar">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Vehicle Status</div>
            <div className="mt-1 flex items-center gap-2 font-display text-xl font-semibold text-emerald-600 dark:text-emerald-400"><span className="h-2 w-2 rounded-full bg-emerald-400" /> Connected</div>
          </div>
          <Telemetry value={v.speed} unit="mph" label="Current Speed" accent />
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Location</div>
            {hasFix ? (
              <>
                {/* Place name first, coordinates as the supporting line. Both
                    lines used to be the coordinates, because the vehicle
                    payload carries no address — it is resolved separately. */}
                <div className="mt-1 text-sm font-medium">
                  {address || (resolving ? "Resolving address…" : "Address unavailable")}
                </div>
                <div className="font-telemetry text-xs text-muted-foreground">{coords}</div>
              </>
            ) : (
              <div className="mt-1 text-sm font-medium text-muted-foreground">No GPS fix yet</div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Heading</div>
            {v.heading == null ? (
              <div className="mt-1 text-sm font-medium text-muted-foreground">Unknown</div>
            ) : (
              <div className="mt-1 flex items-center gap-2 text-sm font-medium">
                {/* lucide's Navigation arrow points north at 0°, so the raw
                    heading is the correct rotation with no offset. */}
                <Navigation className="h-4 w-4 shrink-0" style={{ transform: `rotate(${v.heading}deg)` }} />
                <span>{compassDirection(v.heading)}</span>
                <span className="font-telemetry text-xs text-muted-foreground">{v.heading}°</span>
              </div>
            )}
          </div>
          <div><div className="text-xs uppercase tracking-wider text-muted-foreground">Last Updated</div><div className="mt-1 text-sm font-medium">{relativeTime(v.lastSeen)}</div></div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Nearby geofences</div>
            <div className="mt-2 space-y-1.5">{near.map((g) => <div key={g.id} className="flex items-center justify-between rounded-lg border border-border/50 bg-secondary/20 px-3 py-2 text-sm"><span>{g.name}</span><span className="text-xs text-muted-foreground capitalize">{g.type}</span></div>)}</div>
          </div>
        </div>
        <div className="mt-auto grid grid-cols-2 gap-2 border-t border-border/60 p-4">
          <Button variant="secondary" data-testid="center-vehicle"><Crosshair className="mr-1.5 h-4 w-4" /> Center</Button>
          <Button variant={route ? "default" : "secondary"} onClick={() => setRoute((r) => !r)} data-testid="show-route"><RouteIcon className="mr-1.5 h-4 w-4" /> {route ? "Hide Route" : "Show Route"}</Button>
        </div>
      </Card>
    </div>
  );
}

// tri-state, not boolean — "unknown" (the common case for a real Bouncie
// device with no open low-battery alert) is neither a confirmed "good" nor
// a "warn", so it gets its own neutral styling rather than being folded
// into either.
const BATTERY_LABEL = {
  good: "Battery Good", low: "Battery Low", critical: "Battery Critical", unknown: "Battery Unknown",
};
// Bouncie separates "low" (12V) from "critical" (11.5V), so the UI does too —
// amber for the warning, red for the one that takes the device offline.
const BATTERY_TONE = { good: "ok", low: "caution", critical: "warn", unknown: "neutral" };
const TONE_CLASS = {
  ok: { box: "border-border/60 bg-secondary/20", icon: "text-emerald-600 dark:text-emerald-400", text: "", bubble: "bg-emerald-400/10 text-emerald-600 dark:text-emerald-400" },
  caution: { box: "border-amber-400/25 bg-amber-400/10", icon: "text-amber-600 dark:text-amber-400", text: "text-amber-700 dark:text-amber-300", bubble: "bg-amber-400/10 text-amber-600 dark:text-amber-400" },
  warn: { box: "border-red-500/25 bg-red-500/10", icon: "text-red-600 dark:text-red-400", text: "text-red-700 dark:text-red-300", bubble: "bg-red-500/10 text-red-600 dark:text-red-400" },
  neutral: { box: "border-border/60 bg-secondary/20", icon: "text-muted-foreground", text: "text-muted-foreground", bubble: "bg-secondary/40 text-muted-foreground" },
};

function VehicleHealth({ v, alerts }) {
  const checkEngine = alerts.some((a) => a.type === "Check Engine");
  const batteryTone = BATTERY_TONE[v.batteryStatus] || "neutral";
  const items = [
    { label: v.status === "offline" ? "Disconnected" : "Connected", tone: v.status !== "offline" ? "ok" : "warn", icon: CheckCircle2 },
    { label: BATTERY_LABEL[v.batteryStatus] || "Battery Unknown", tone: batteryTone, icon: Battery },
    { label: checkEngine ? "Check Engine" : "Engine Normal", tone: checkEngine ? "warn" : "ok", icon: Wrench },
    { label: "Device Secure", tone: "ok", icon: ShieldCheck },
  ];
  return (
    <div className="space-y-6">
      <Card className="border-border/70 bg-card/60 p-5">
        <div className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">Vehicle Health</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((i) => (
            <div key={i.label} className={cn("flex items-center gap-3 rounded-xl border p-4", TONE_CLASS[i.tone].box)}>
              <i.icon className={cn("h-5 w-5", TONE_CLASS[i.tone].icon)} />
              <span className={cn("text-sm font-medium", TONE_CLASS[i.tone].text)}>{i.label}</span>
            </div>
          ))}
        </div>
      </Card>
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="flex flex-col items-center justify-center gap-2 border-border/70 bg-card/60 p-5">
          <div className={cn("flex h-14 w-14 items-center justify-center rounded-full", TONE_CLASS[batteryTone].bubble)}>
            <Battery className="h-6 w-6" />
          </div>
          <div className="text-sm font-medium">{BATTERY_LABEL[v.batteryStatus] || "Battery Unknown"}</div>
          <div className="text-sm font-medium">Battery</div>
        </Card>
        <Gauge2 label="Fuel" value={Math.round(v.fuel)} unit="%" tone={v.fuel < 25 ? "amber" : "sky"} />
        <StatItem label="Device Signal" value={v.device.signal != null ? `${v.device.signal}%` : "—"} sub={v.device.firmware ? `Firmware ${v.device.firmware}` : "Firmware unknown"} className="flex flex-col justify-center" />
      </div>
      {checkEngine && (
        <Card className="border-red-500/25 bg-red-500/5 p-5">
          <div className="flex items-center gap-2 font-display text-lg font-semibold text-red-600 dark:text-red-400"><Wrench className="h-5 w-5" /> CHECK ENGINE</div>
          <p className="mt-1.5 text-sm text-muted-foreground">{alerts.find((a) => a.type === "Check Engine")?.description}</p>
        </Card>
      )}
    </div>
  );
}

function Gauge2({ label, value, unit, tone }) {
  const color = tone === "red" ? "#f43f5e" : tone === "amber" ? "#fbbf24" : tone === "sky" ? "#38bdf8" : "#34d399";
  const r = 42, c = 2 * Math.PI * r, off = c - (value / 100) * c;
  return (
    <Card className="flex flex-col items-center border-border/70 bg-card/60 p-5">
      <div className="relative h-28 w-28">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
          <circle cx="50" cy="50" r={r} fill="none" stroke="hsl(var(--secondary))" strokeWidth="8" />
          <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="font-display text-2xl font-semibold tabular-nums">{value}</span><span className="text-xs text-muted-foreground">{unit}</span></div>
      </div>
      <div className="mt-3 text-sm font-medium">{label}</div>
    </Card>
  );
}

function VehicleHistory({ trips, alerts }) {
  const events = [
    ...trips.map((t) => ({ time: t.startTime, offsetMinutes: t.utcOffsetMinutes, type: "trip", label: `Trip: ${t.startLoc} → ${t.endLoc}`, sub: fmtKm(t.distanceKm) })),
    ...alerts.map((a) => ({ time: a.time, offsetMinutes: null, type: "alert", label: a.type, sub: a.description, sev: a.severity })),
  ].sort((x, y) => y.time - x.time);
  if (events.length === 0) return <EmptyState title="No history" description="No recorded activity for this vehicle." />;
  return (
    <div className="relative space-y-4 pl-6">
      <div className="absolute left-[9px] top-2 bottom-2 w-px bg-border/70" />
      {events.map((e, i) => (
        <div key={i} className="relative">
          <div className={cn("absolute -left-[18px] top-1.5 h-3 w-3 rounded-full ring-4 ring-background", e.type === "alert" ? (e.sev === "critical" ? "bg-red-500" : "bg-amber-400") : "bg-emerald-400")} />
          <div className="rounded-xl border border-border/60 bg-card/40 p-3">
            <div className="flex items-center justify-between gap-2"><span className="text-sm font-medium">{e.label}</span><span className="text-xs text-muted-foreground">{clockTime(e.time, e.offsetMinutes)}</span></div>
            <p className="mt-0.5 text-xs text-muted-foreground">{e.sub}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
